/**
 * Admin voice recording page — Sophie & Atsuko record native pronunciations
 * that override any AI TTS instantly across the site.
 *
 * Flow per row:
 *  1. Click 🎙️ to record via browser mic (MediaRecorder API → webm/opus → we
 *     just send the blob as-is; browsers accept audio/webm in <audio>). To be
 *     safe we ALSO accept MP3 file upload.
 *  2. Preview.
 *  3. Save → POST /api/admin/voice-upload (multipart).
 *  4. Green check appears; site instantly serves this recording (Tier 0 in
 *     tts_service).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, Play, Upload, Trash2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const HIRAGANA = [
  ["a","あ"],["i","い"],["u","う"],["e","え"],["o","お"],
  ["ka","か"],["ki","き"],["ku","く"],["ke","け"],["ko","こ"],
  ["sa","さ"],["shi","し"],["su","す"],["se","せ"],["so","そ"],
  ["ta","た"],["chi","ち"],["tsu","つ"],["te","て"],["to","と"],
  ["na","な"],["ni","に"],["nu","ぬ"],["ne","ね"],["no","の"],
  ["ha","は"],["hi","ひ"],["fu","ふ"],["he","へ"],["ho","ほ"],
  ["ma","ま"],["mi","み"],["mu","む"],["me","め"],["mo","も"],
  ["ya","や"],["yu","ゆ"],["yo","よ"],
  ["ra","ら"],["ri","り"],["ru","る"],["re","れ"],["ro","ろ"],
  ["wa","わ"],["wo","を"],["n","ん"],
];

const KATAKANA = [
  ["a","ア"],["i","イ"],["u","ウ"],["e","エ"],["o","オ"],
  ["ka","カ"],["ki","キ"],["ku","ク"],["ke","ケ"],["ko","コ"],
  ["sa","サ"],["shi","シ"],["su","ス"],["se","セ"],["so","ソ"],
  ["ta","タ"],["chi","チ"],["tsu","ツ"],["te","テ"],["to","ト"],
  ["na","ナ"],["ni","ニ"],["nu","ヌ"],["ne","ネ"],["no","ノ"],
  ["ha","ハ"],["hi","ヒ"],["fu","フ"],["he","ヘ"],["ho","ホ"],
  ["ma","マ"],["mi","ミ"],["mu","ム"],["me","メ"],["mo","モ"],
  ["ya","ヤ"],["yu","ユ"],["yo","ヨ"],
  ["ra","ラ"],["ri","リ"],["ru","ル"],["re","レ"],["ro","ロ"],
  ["wa","ワ"],["wo","ヲ"],["n","ン"],
];

function useMicRecorder() {
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (recording) return null;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";
    const rec = new MediaRecorder(stream, { mimeType: mime });
    chunksRef.current = [];
    rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    rec.start();
    mediaRef.current = { rec, stream };
    setRecording(true);
  };

  const stop = () => new Promise((resolve) => {
    if (!mediaRef.current) return resolve(null);
    const { rec, stream } = mediaRef.current;
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType });
      stream.getTracks().forEach((t) => t.stop());
      mediaRef.current = null;
      setRecording(false);
      resolve(blob);
    };
    rec.stop();
  });

  return { recording, start, stop };
}

function VoiceRow({ romaji, char, voice, adminKey, hasNative: hasNativeProp, onSaved }) {
  const [hasNative, setHasNative] = useState(hasNativeProp);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const audioRef = useRef(null);
  const mic = useMicRecorder();

  useEffect(() => { setHasNative(hasNativeProp); }, [hasNativeProp]);

  const upload = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", char);
      fd.append("voice", voice);
      // If webm from mic, keep it - browsers accept it. If mp3, keep .mp3
      fd.append("audio", blob, blob.type.includes("mp3") ? "clip.mp3" : "clip.webm");
      const r = await fetch(`${API}/api/admin/voice-upload?key=${encodeURIComponent(adminKey)}`, {
        method: "POST", body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "upload failed");
      toast.success(`✅ ${char} enregistré`);
      setHasNative(true);
      setPendingBlob(null);
      setPreviewUrl(null);
      onSaved && onSaved(char);
    } catch (e) {
      toast.error(`Erreur: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!confirm(`Supprimer l'enregistrement natif de "${char}" ?`)) return;
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/admin/voice-upload?text=${encodeURIComponent(char)}&voice=${voice}&key=${encodeURIComponent(adminKey)}`,
        { method: "DELETE" },
      );
      if (r.ok) {
        toast.success(`🗑 ${char} supprimé — retour à l'IA`);
        setHasNative(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingBlob(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onRecord = async () => {
    if (mic.recording) {
      const blob = await mic.stop();
      if (blob) {
        setPendingBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
      }
    } else {
      await mic.start();
    }
  };

  const playServerAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const a = new Audio(`${API}/api/tts?text=${encodeURIComponent(char)}&voice=${voice}&v=native`);
    audioRef.current = a;
    a.play().catch(() => {});
  };

  return (
    <div
      className={`p-3 rounded-xl border-2 flex items-center gap-2 ${
        hasNative ? "border-emerald-500 bg-emerald-50" : "border-[#1A2A40]/10 bg-white"
      }`}
      data-testid={`row-${voice}-${romaji}`}
    >
      <div className="w-14 text-center">
        <div className="text-2xl font-bold text-[#1A2A40]">{char}</div>
        <div className="text-[10px] text-[#1A2A40]/60 uppercase">{romaji}</div>
      </div>
      <div className="flex-1 flex items-center gap-1.5 flex-wrap">
        {hasNative ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        ) : (
          <XCircle className="w-5 h-5 text-[#1A2A40]/20" />
        )}
        <button
          onClick={onRecord}
          disabled={busy}
          className={`px-2 py-1 rounded-lg text-xs font-bold text-white transition-colors ${
            mic.recording ? "bg-red-500 animate-pulse" : "bg-[#E94A3F] hover:bg-[#c93a30]"
          }`}
          title={mic.recording ? "Stop" : "Enregistrer"}
        >
          {mic.recording ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
        </button>
        <label className="px-2 py-1 rounded-lg text-xs font-bold bg-[#1A2A40] text-white cursor-pointer hover:bg-[#0d1520]">
          <Upload className="w-3.5 h-3.5 inline" />
          <input type="file" accept="audio/*,.mp3,.webm,.wav,.ogg" className="hidden" onChange={onFile} />
        </label>
        {previewUrl && (
          <>
            <audio src={previewUrl} controls className="h-7 w-40" />
            <button
              onClick={() => upload(pendingBlob)}
              disabled={busy}
              className="px-2 py-1 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Save
            </button>
          </>
        )}
        {hasNative && !previewUrl && (
          <>
            <button
              onClick={playServerAudio}
              className="px-2 py-1 rounded-lg text-xs font-bold bg-[#1A2A40]/10 text-[#1A2A40] hover:bg-[#1A2A40]/20"
            >
              <Play className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={del}
              disabled={busy}
              className="px-2 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-700 hover:bg-red-200"
              title="Supprimer"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminVoice() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem("adminKey") || "");
  const [voice, setVoice] = useState("female");
  const [tab, setTab] = useState("hiragana");
  const [existing, setExisting] = useState(new Set()); // set of file keys uploaded
  const [loading, setLoading] = useState(false);

  const list = tab === "hiragana" ? HIRAGANA : KATAKANA;

  // sha1 helper for detecting which chars are already recorded
  const sha1 = async (s) => {
    const buf = new TextEncoder().encode(s);
    const h = await crypto.subtle.digest("SHA-1", buf);
    return Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const refresh = async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/voice-list?key=${encodeURIComponent(adminKey)}`);
      if (!r.ok) {
        if (r.status === 403) toast.error("Clé admin invalide");
        return;
      }
      const j = await r.json();
      setExisting(new Set(j.files.map((f) => f.key)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [adminKey]);

  const [expectedKeys, setExpectedKeys] = useState({});
  useEffect(() => {
    (async () => {
      const map = {};
      for (const [, ch] of [...HIRAGANA, ...KATAKANA]) {
        for (const v of ["female", "male"]) {
          map[`${v}|${ch}`] = await sha1(`user|${v}|${ch}`);
        }
      }
      setExpectedKeys(map);
    })();
  }, []);

  const count = useMemo(() => {
    let n = 0;
    for (const [, ch] of list) {
      if (existing.has(expectedKeys[`${voice}|${ch}`])) n++;
    }
    return n;
  }, [list, existing, expectedKeys, voice]);

  const deleteAllForVoice = async () => {
    const msg = `⚠️ Supprimer TOUS les enregistrements ${voice === "female" ? "féminins" : "masculins"} (hiragana + katakana) ?\n\nUtile pour recommencer proprement. L'IA reprendra la main jusqu'à nouveaux enregistrements.`;
    if (!confirm(msg)) return;
    setLoading(true);
    let deleted = 0;
    for (const [, ch] of [...HIRAGANA, ...KATAKANA]) {
      try {
        const r = await fetch(
          `${API}/api/admin/voice-upload?text=${encodeURIComponent(ch)}&voice=${voice}&key=${encodeURIComponent(adminKey)}`,
          { method: "DELETE" },
        );
        const j = await r.json();
        if (j.ok) deleted++;
      } catch (_e) { /* noop */ }
    }
    toast.success(`🗑 ${deleted} enregistrements supprimés — reset propre`);
    await refresh();
    setLoading(false);
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen p-8 washi-bg" data-testid="admin-voice-login">
        <div className="max-w-md mx-auto p-6 rounded-2xl bg-white border-2 border-[#1A2A40]/10">
          <h1 className="text-2xl font-bold text-[#1A2A40] mb-4">Admin — Voix natives</h1>
          <label className="block text-sm mb-2">Clé admin</label>
          <input
            type="password"
            className="w-full px-3 py-2 border-2 border-[#1A2A40]/20 rounded-lg mb-3"
            placeholder="ADMIN_KEY..."
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                localStorage.setItem("adminKey", e.currentTarget.value);
                setAdminKey(e.currentTarget.value);
              }
            }}
          />
          <p className="text-xs text-[#1A2A40]/60">Appuie Entrée pour valider. Stockée localement.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-8 washi-bg" data-testid="admin-voice-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-3xl font-bold text-[#1A2A40]">Voix natives — {voice === "female" ? "Atsuko" : "Alexis (homme)"}</h1>
          <div className="flex items-center gap-3">
            <div className="text-sm text-[#1A2A40]/70">
              {count}/{list.length} enregistrés {loading && "…"}
            </div>
            <button
              onClick={deleteAllForVoice}
              disabled={loading}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-100 text-red-700 hover:bg-red-200 border border-red-300 inline-flex items-center gap-1"
              data-testid="delete-all-voice-btn"
              title="Supprimer tous les enregistrements de cette voix (hiragana + katakana) pour recommencer"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Tout supprimer ({voice})
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setVoice("female")}
            className={`px-4 py-2 rounded-xl text-sm font-bold ${voice === "female" ? "bg-[#E94A3F] text-white" : "bg-white text-[#1A2A40] border-2 border-[#1A2A40]/10"}`}
            data-testid="voice-female"
          >
            Voix féminine (Sophie/Atsuko)
          </button>
          <button
            onClick={() => setVoice("male")}
            className={`px-4 py-2 rounded-xl text-sm font-bold ${voice === "male" ? "bg-[#E94A3F] text-white" : "bg-white text-[#1A2A40] border-2 border-[#1A2A40]/10"}`}
            data-testid="voice-male"
          >
            Voix masculine
          </button>
          <div className="w-full" />
          <button
            onClick={() => setTab("hiragana")}
            className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === "hiragana" ? "bg-[#1A2A40] text-white" : "bg-white text-[#1A2A40] border-2 border-[#1A2A40]/10"}`}
          >
            Hiragana ({HIRAGANA.length})
          </button>
          <button
            onClick={() => setTab("katakana")}
            className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === "katakana" ? "bg-[#1A2A40] text-white" : "bg-white text-[#1A2A40] border-2 border-[#1A2A40]/10"}`}
          >
            Katakana ({KATAKANA.length})
          </button>
        </div>

        <div className="mb-4 p-4 rounded-xl bg-[#FFF8E1] border-2 border-amber-300 text-sm">
          <b>Comment ça marche :</b> Clique 🎙️ pour enregistrer via le micro OU 📤 pour uploader un MP3.
          Une fois sauvegardé (✅ vert), le site sert IMMÉDIATEMENT cette voix humaine au lieu de l'IA.
          Pas besoin de redéployer.
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {list.map(([romaji, char]) => {
            const has = existing.has(expectedKeys[`${voice}|${char}`]);
            return (
              <VoiceRow
                key={`${voice}-${char}`}
                romaji={romaji}
                char={char}
                voice={voice}
                adminKey={adminKey}
                hasNative={has}
                onSaved={refresh}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
